import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '../ui/badge'
import { worksiteCostLow, worksiteCostHigh, formatCost } from '../../lib/capex-utils'
import { useFormatters } from '../../lib/format'
import type { Worksite, UnlockedRent } from './types'
import { useCapexCurrency } from './CapexCurrencyContext'

interface KPIStripProps {
  worksites: Worksite[]
  breachCount?: number
  overdueCount?: number
  unlockedRent?: UnlockedRent
}

export const KPIStrip = React.memo(function KPIStrip({
  worksites,
  breachCount = 0,
  overdueCount = 0,
  unlockedRent,
}: KPIStripProps) {
  const capexCurrency = useCapexCurrency()
  const { t } = useTranslation('capex')
  const { formatMoney } = useFormatters()

  const { totalNeeds, validatedBudget } = useMemo(() => {
    const active = worksites.filter((ws) => ws.status !== 'ignored')
    const total = active.reduce(
      (sum, ws) => sum + (worksiteCostLow(ws) + worksiteCostHigh(ws)) / 2,
      0
    )
    const validated = worksites
      .filter((ws) => ws.status === 'validated')
      .reduce((sum, ws) => sum + (worksiteCostLow(ws) + worksiteCostHigh(ws)) / 2, 0)
    return { totalNeeds: total, validatedBudget: validated }
  }, [worksites])

  return (
    <div className="flex flex-wrap items-center gap-4 text-sm">
      <div
        className="flex items-center gap-1.5"
        data-testid="kpi-total-needs"
        aria-label={`${t('kpi.total_needs', 'Total needs')}: ${formatCost(totalNeeds, capexCurrency)}`}
      >
        <span className="text-muted-foreground">{t('kpi.total_needs', 'Total needs')}:</span>
        <span className="font-semibold text-foreground">{formatCost(totalNeeds, capexCurrency)}</span>
      </div>

      <div
        className="flex items-center gap-1.5"
        data-testid="kpi-validated-budget"
        aria-label={`${t('kpi.validated_budget', 'Validated budget')}: ${formatCost(validatedBudget, capexCurrency)}`}
      >
        <span className="text-muted-foreground">{t('kpi.validated_budget', 'Validated budget')}:</span>
        <span className="font-semibold text-foreground">{formatCost(validatedBudget, capexCurrency)}</span>
      </div>

      {unlockedRent && unlockedRent.lease_count > 0 && (
        <div
          className="flex items-center gap-1.5"
          data-testid="capex-kpi-unlocked-rent"
        >
          <span className="text-muted-foreground">{t('kpi.unlocked_rent')}:</span>
          <span className="font-semibold text-foreground">
            {formatMoney(unlockedRent.rent_at_risk, unlockedRent.currency)}
          </span>
          {unlockedRent.rent_unverified > 0 && (
            <span className="text-xs text-muted-foreground">
              (+{formatMoney(unlockedRent.rent_unverified, unlockedRent.currency)} {t('kpi.unlocked_rent_unverified')})
            </span>
          )}
        </div>
      )}

      {breachCount > 0 && (
        <Badge
          variant="destructive"
          className="text-xs"
          data-testid="kpi-breaches"
          aria-label={`${t('kpi.breaches', 'Breaches')}: ${breachCount}`}
        >
          {t('kpi.breaches', 'Breaches')}: {breachCount}
        </Badge>
      )}

      {overdueCount > 0 && (
        <Badge
          className="bg-orange-100 text-orange-800 hover:bg-orange-100 text-xs"
          data-testid="kpi-overdue"
          aria-label={`${t('kpi.overdue', 'Regulatory overruns')}: ${overdueCount}`}
        >
          {t('kpi.overdue', 'Regulatory overruns')}: {overdueCount}
        </Badge>
      )}
    </div>
  )
})
